// src/components/GenerateAdCreativeModal.jsx
import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { X, Loader2, RefreshCcw, Info, Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { useAuth } from "@clerk/clerk-react";

const steps = [
  { number: 1, label: "Assets" },
  { number: 2, label: "Texts" },
  { number: 3, label: "Template" },
];

const LIMITS = {
  headline: 40,
  punchline: 40,
  cta: 25,
};

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  "https://ecom-ads-ai-backend-production.up.railway.app";

/* ------------------------------ helpers ------------------------------ */
function getAssetUrl(asset) {
  return (asset && typeof asset === "object" ? asset.url : asset) || "";
}

function looksLikeBlockedMedia(url) {
  const u = String(url || "").toLowerCase();
  return u.includes(".mp4") || u.includes(".gif");
}

function gcd(a, b) {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
}

function ratioTextFromWH(w, h) {
  const g = gcd(w, h);
  const rw = Math.round(w / g);
  const rh = Math.round(h / g);
  return `${rw}:${rh}`;
}

export default function GenerateAdCreativeModal({
  projectId,
  projectAssets,
  onClose,
  onCreated,
}) {
  const { getToken } = useAuth();

  const [step, setStep] = useState(1);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const [headline, setHeadline] = useState("");
  const [punchline, setPunchline] = useState("");
  const [cta, setCta] = useState("");

  // Per-field regen loaders
  const [regenHeadlineLoading, setRegenHeadlineLoading] = useState(false);
  const [regenPunchlineLoading, setRegenPunchlineLoading] = useState(false);
  const [regenCtaLoading, setRegenCtaLoading] = useState(false);

  // Initial AI text loading (greys out all inputs on step 2 load)
  const [textsLoading, setTextsLoading] = useState(false);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);

  // ✅ Filters
  const [collectionFilter, setCollectionFilter] = useState("all");
  const [sizeFilter, setSizeFilter] = useState("all");

  // ✅ All sizes (even if templates paged)
  const [allSizes, setAllSizes] = useState([]); // [{width,height}] | ["1200x628", ...] supported

  // Template list loading + generic error
  const [loading, setLoading] = useState(false); // initial load
  const [loadingMore, setLoadingMore] = useState(false); // infinite load
  const [error, setError] = useState("");

  // cursor pagination
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(true);

  // ✅ Step 1 manual upload (copied from AdMarketing Step 1 behavior)
  const [step1UploadedAsset, setStep1UploadedAsset] = useState(null); // { id, url } | null
  const [step1UploadLoading, setStep1UploadLoading] = useState(false);
  const step1FileInputRef = useRef(null);

  // scroll root for infinite loader
  const scrollRef = useRef(null);
  const loadMoreRef = useRef(null);

  // ✅ hard lock to prevent multi-fire IntersectionObserver storms
  const loadMoreLockRef = useRef(false);

  const StepFooter = ({ left, right }) => (
    <div className="absolute bottom-0 left-0 w-full bg-[#191930] border-t border-[#2B2B4A] flex justify-between items-center px-8 py-4 z-20">
      <div>{left}</div>
      <div className="flex items-center gap-3">{right}</div>
    </div>
  );

  // ✅ Filter out mp4/gifs from selectable assets in this modal
  const filteredProjectAssets = useMemo(() => {
    const list = Array.isArray(projectAssets) ? projectAssets : [];
    return list.filter((a) => {
      const url = getAssetUrl(a);
      if (!url) return false;
      return !looksLikeBlockedMedia(url);
    });
  }, [projectAssets]);

  // If selected asset becomes invalid (mp4/gif), clear it (unless it's the uploaded tile)
  useEffect(() => {
    const selUrl = getAssetUrl(selectedAsset);
    const uploadedUrl = getAssetUrl(step1UploadedAsset);
    if (selUrl && selUrl !== uploadedUrl && looksLikeBlockedMedia(selUrl)) {
      setSelectedAsset(null);
    }
  }, [selectedAsset, step1UploadedAsset]);

  // ✅ Do we actually have any collection metadata?
  const hasCollections = useMemo(() => {
    return (templates || []).some(
      (t) => Boolean(t.collectionUuid) || Boolean(t.collectionName)
    );
  }, [templates]);

  // ✅ Collection dropdown options (only meaningful if Placid returns collection data)
  const collectionOptions = useMemo(() => {
    if (!hasCollections) return [{ value: "all", label: "All collections" }];

    const map = new Map();

    (templates || []).forEach((t) => {
      const id = t.collectionUuid || "";
      const name = t.collectionName || "";

      const key = id || "__uncategorized__";
      const label = name || "Uncategorized";

      if (!map.has(key)) map.set(key, { value: key, label });
    });

    const sorted = Array.from(map.values()).sort((a, b) =>
      String(a.label).localeCompare(String(b.label))
    );

    return [{ value: "all", label: "All collections" }, ...sorted];
  }, [templates, hasCollections]);

  // ✅ Size dropdown options (MUST show all sizes even if templates are paged)
  const sizeOptions = useMemo(() => {
    const entries = [];

    if (Array.isArray(allSizes) && allSizes.length > 0) {
      allSizes.forEach((s) => {
        if (typeof s === "string") {
          const [wRaw, hRaw] = s.split("x");
          const w = Number(wRaw);
          const h = Number(hRaw);
          if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
            return;
          entries.push({ w, h, value: `${w}x${h}` });
          return;
        }

        const w = Number(s?.width);
        const h = Number(s?.height);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
          return;
        entries.push({ w, h, value: `${w}x${h}` });
      });
    } else {
      (templates || []).forEach((t) => {
        const w = Number(t.width);
        const h = Number(t.height);
        if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0)
          return;
        entries.push({ w, h, value: `${w}x${h}` });
      });
    }

    // unique
    const map = new Map();
    entries.forEach((e) => {
      if (!map.has(e.value)) {
        map.set(e.value, {
          value: e.value,
          w: e.w,
          h: e.h,
          label: `${e.w}×${e.h} • ${ratioTextFromWH(e.w, e.h)}`,
        });
      }
    });

    const sorted = Array.from(map.values()).sort((a, b) => {
      const areaA = (a.w || 0) * (a.h || 0);
      const areaB = (b.w || 0) * (b.h || 0);
      return areaB - areaA;
    });

    return [
      { value: "all", label: "All sizes" },
      ...sorted.map(({ value, label }) => ({ value, label })),
    ];
  }, [allSizes, templates]);

  const filteredTemplates = useMemo(() => {
    // keep existing default behavior: show only image-capable templates
    let list = (templates || []).filter((t) => Boolean(t.imageCapable));

    if (sizeFilter !== "all") {
      list = list.filter((t) => `${t.width}x${t.height}` === sizeFilter);
    }

    if (hasCollections && collectionFilter !== "all") {
      list = list.filter((t) => {
        const key = t.collectionUuid || "__uncategorized__";
        return key === collectionFilter;
      });
    }

    return list;
  }, [templates, sizeFilter, hasCollections, collectionFilter]);

  // If current selected template is excluded by filters, clear it
  useEffect(() => {
    if (!selectedTemplate) return;
    const stillVisible = (filteredTemplates || []).some(
      (t) => String(t.id) === String(selectedTemplate.id)
    );
    if (!stillVisible) setSelectedTemplate(null);
  }, [filteredTemplates, selectedTemplate]);

  // ---------------- Fetch templates (Step 3 / paged) ----------------
  const mapTemplates = useCallback((list) => {
    return (list || []).map((tpl) => {
      const fallbackCapable = (tpl.layers || []).some((l) =>
        ["picture", "browserframe"].includes(String(l.type || "").toLowerCase())
      );
      return {
        id: tpl.template_uuid,
        name: tpl.name,
        preview_image: tpl.thumbnail_url,
        width: tpl.width,
        height: tpl.height,
        layers: tpl.layers || [],
        imageCapable:
          typeof tpl.image_capable === "boolean"
            ? tpl.image_capable
            : fallbackCapable,

        collectionUuid: tpl.collection_uuid || null,
        collectionName: tpl.collection_name || null,
      };
    });
  }, []);

  const fetchTemplatesPage = useCallback(
    async ({ cursor = null, append = false } = {}) => {
      if (append) setLoadingMore(true);
      else setLoading(true);

      setError("");

      try {
        const token = await getToken();
        const cursorUsed = cursor || null;

        const res = await axios.get(`${BACKEND_URL}/api/adcreatives/list`, {
          headers: { Authorization: `Bearer ${token}` },
          params: cursorUsed ? { cursor: cursorUsed } : undefined,
        });

        const mapped = mapTemplates(res.data.templates || []);
        const incomingSizes = res.data.sizes || [];

        if (Array.isArray(incomingSizes) && incomingSizes.length > 0) {
          setAllSizes(incomingSizes);
        }

        const next = res.data.next_cursor || null;

        // ✅ Safety: if backend keeps returning the SAME cursor, stop paging to avoid loops
        if (cursorUsed && next && String(next) === String(cursorUsed)) {
          setNextCursor(null);
          setHasMore(false);
        } else if (!next || mapped.length === 0) {
          setNextCursor(null);
          setHasMore(false);
        } else {
          setNextCursor(next);
          setHasMore(true);
        }

        setTemplates((prev) => {
          if (!append) return mapped;

          // dedupe by id
          const seen = new Set((prev || []).map((t) => String(t.id)));
          const out = [...(prev || [])];
          mapped.forEach((t) => {
            const key = String(t.id);
            if (!seen.has(key)) {
              seen.add(key);
              out.push(t);
            }
          });
          return out;
        });

        // If we started a fresh load, reset filters to sane defaults (same intent as before)
        if (!append) {
          setCollectionFilter("all");
          setSizeFilter("all");
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          "Failed to fetch templates:",
          err.response?.data || err.message
        );
        setError(
          `Failed to fetch templates${
            err.response?.status ? ` (HTTP ${err.response.status})` : ""
          }.`
        );
        setHasMore(false);
        setNextCursor(null);
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);

        // ✅ release lock (append + initial)
        loadMoreLockRef.current = false;
      }
    },
    [getToken, mapTemplates]
  );

  const loadMore = useCallback(() => {
    if (loading || loadingMore) return;
    if (!hasMore || !nextCursor) return;

    // ✅ lock prevents rapid re-entry while state updates + request in flight
    if (loadMoreLockRef.current) return;
    loadMoreLockRef.current = true;

    fetchTemplatesPage({ cursor: nextCursor, append: true });
  }, [loading, loadingMore, hasMore, nextCursor, fetchTemplatesPage]);

  // Infinite loader observer (root = modal scroll container)
  useEffect(() => {
    if (step !== 3) return;
    if (!hasMore) return;

    const root = scrollRef.current;
    const target = loadMoreRef.current;
    if (!root || !target) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries?.[0];
        if (!first?.isIntersecting) return;
        loadMore();
      },
      { root, rootMargin: "250px", threshold: 0 }
    );

    obs.observe(target);
    return () => obs.disconnect();
  }, [step, hasMore, loadMore]);

  // ---------------- Initial AI texts (Step 2) ----------------
  const generateTexts = useCallback(async () => {
    setTextsLoading(true);
    setError("");
    try {
      const token = await getToken();
      const res = await axios.post(
        `${BACKEND_URL}/api/adcreatives/generate-texts`,
        { projectId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHeadline(res.data.headline || "");
      setPunchline(res.data.punchline || "");
      setCta(res.data.cta || "");
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Failed to generate texts:", err.response?.data || err.message);
      setError(
        `Failed to generate texts${
          err.response?.status ? ` (HTTP ${err.response.status})` : ""
        }.`
      );
    } finally {
      setTextsLoading(false);
    }
  }, [getToken, projectId]);

  // ---------------- Per-field regenerate ----------------
  const regenerateField = useCallback(
    async (field) => {
      const setLoadingFlag = {
        headline: setRegenHeadlineLoading,
        punchline: setRegenPunchlineLoading,
        cta: setRegenCtaLoading,
      }[field];

      if (!setLoadingFlag) return;

      setLoadingFlag(true);
      setError("");
      try {
        const token = await getToken();
        const res = await axios.post(
          `${BACKEND_URL}/api/adcreatives/generate-texts`,
          { projectId },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (field === "headline") setHeadline(res.data.headline || "");
        if (field === "punchline") setPunchline(res.data.punchline || "");
        if (field === "cta") setCta(res.data.cta || "");
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          "Failed to regenerate text:",
          field,
          err.response?.data || err.message
        );
        setError(
          `Failed to regenerate ${field}${
            err.response?.status ? ` (HTTP ${err.response.status})` : ""
          }.`
        );
      } finally {
        setLoadingFlag(false);
      }
    },
    [getToken, projectId]
  );

  // ---------------- Step transitions ----------------
  useEffect(() => {
    if (step === 2) generateTexts();
    if (step === 3) {
      // reset lock before fresh list
      loadMoreLockRef.current = false;

      setTemplates([]);
      setSelectedTemplate(null);
      setNextCursor(null);
      setHasMore(true);
      fetchTemplatesPage({ cursor: null, append: false });
    }
  }, [step, generateTexts, fetchTemplatesPage]);

  // ✅ Step 1 manual upload handler (same behavior as AdMarketing Step 1)
  const handleStep1Upload = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setStep1UploadLoading(true);
      setError("");

      try {
        const isGif =
          String(file.type || "").toLowerCase() === "image/gif" ||
          String(file.name || "").toLowerCase().endsWith(".gif");
        if (isGif) {
          setError("GIFs are not supported. Please upload JPG, PNG, or WEBP.");
          return;
        }

        if (file.size > 20 * 1024 * 1024) {
          setError("Image is too large. Please upload a file under 20MB.");
          return;
        }

        const token = await getToken();
        const formData = new FormData();
        formData.append("file", file);

        const { data } = await axios.post(
          `${BACKEND_URL}/api/videos/marketing/endcard/upload`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const url = data?.url;
        if (url) {
          const newAsset = {
            id: `asset-upload-${Date.now()}`,
            url,
          };

          setStep1UploadedAsset(newAsset);
          setSelectedAsset(newAsset);
        }
      } catch {
        setError("Failed to upload image.");
      } finally {
        setStep1UploadLoading(false);
        event.target.value = "";
      }
    },
    [getToken]
  );

  // ---------------- Helpers: character limits & counters ----------------
  const clamp = (val, limit) => (val || "").slice(0, limit);

  const handleTextChange = (setter, limit) => (e) => {
    const next = e.target.value || "";
    setter(clamp(next, limit));
  };

  const countClass = (len, limit) =>
    len >= limit
      ? "text-red-300"
      : len >= Math.max(1, Math.floor(limit * 0.85))
      ? "text-yellow-300"
      : "text-gray-400";

  // ---------------- Generate Creative (fire-and-forget) ----------------
  const handleGenerate = async () => {
    if (
      textsLoading ||
      regenHeadlineLoading ||
      regenPunchlineLoading ||
      regenCtaLoading
    ) {
      // eslint-disable-next-line no-alert
      alert("Please wait for AI text to finish loading.");
      return;
    }

    if (
      !selectedAsset ||
      !headline.trim() ||
      !punchline.trim() ||
      !cta.trim() ||
      !selectedTemplate
    ) {
      // eslint-disable-next-line no-alert
      alert("Please complete all steps before generating.");
      return;
    }

    // Size is inferred from the selected template now
    const inferredSize = `${Number(selectedTemplate.width)}x${Number(
      selectedTemplate.height
    )}`;

    if (onCreated) onCreated({ kickoff: true });
    onClose();

    (async () => {
      try {
        const token = await getToken();
        await axios.post(
          `${BACKEND_URL}/api/adcreatives/generate`,
          {
            projectId,
            assetUrl: selectedAsset.url || selectedAsset,
            headline,
            punchline,
            cta,
            size: inferredSize,
            templateId: selectedTemplate.id,
            templateLayers: selectedTemplate.layers,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (onCreated) onCreated({ followup: true });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(
          "Ad creative generation request failed:",
          err.response?.data || err.message
        );
      }
    })();
  };

  // ---------------- Field UI helper (styled like AdMarketing script step) ----------------
  function Field({
    id,
    label,
    value,
    onChange,
    loadingFlag,
    limit,
    placeholder,
    onRegenerate,
    regenDisabled,
  }) {
    const length = (value || "").length;

    return (
      <div className="mb-5">
        <div className="flex items-center justify-between mb-2">
          <label className="block text-gray-300 flex-1" htmlFor={id}>
            {label}
          </label>

          <div className="flex items-center gap-3">
            <span className={`text-xs ${countClass(length, limit)}`}>
              {length} / {limit}
            </span>

            <button
              type="button"
              onClick={onRegenerate}
              className="p-2 text-pink-400 hover:text-pink-300 rounded-full bg-[#232336] hover:bg-[#2c2c3e] transition disabled:opacity-60"
              disabled={regenDisabled}
              aria-label={`Regenerate ${label}`}
              title={`Regenerate ${label}`}
            >
              <RefreshCcw
                size={18}
                className={regenDisabled ? "animate-spin" : ""}
              />
            </button>
          </div>
        </div>

        <div className="relative">
          <textarea
            id={id}
            value={loadingFlag ? "AI is loading the message, please hold..." : value}
            onChange={onChange}
            maxLength={limit}
            className="w-full min-h-[120px] max-h-72 bg-[#1F1F42] rounded-md p-4 text-white border border-gray-600 focus:border-pink-500 focus:ring-1 focus:ring-pink-600 transition"
            placeholder={placeholder}
            disabled={loadingFlag}
          />
          {loadingFlag && (
            <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/30">
              <div className="flex items-center gap-2 text-white/90 text-sm">
                <Loader2 size={16} className="animate-spin" />
                <span>Loading…</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  const selectedUrl = selectedAsset?.url || selectedAsset || "";
  const isSelected = Boolean(selectedUrl);

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 px-2 py-4">
      <div className="relative bg-[#191930] w-[980px] max-w-[98vw] h-[86vh] rounded-2xl shadow-2xl flex flex-col min-h-[520px] overflow-hidden">
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 bg-white text-black p-2 rounded-full hover:bg-gray-100 z-10"
          aria-label="Close modal"
          type="button"
        >
          <X size={22} />
        </button>

        {/* Stepper */}
        <div className="flex items-center gap-2 px-8 pt-7 pb-2">
          {steps.map(({ number, label }) => (
            <React.Fragment key={number}>
              <span
                className={`rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold
                  ${
                    step === number
                      ? "bg-pink-600 text-white"
                      : "bg-[#29294a] text-gray-400"
                  }`}
              >
                {number}
              </span>
              <span className="text-base sm:text-lg text-white font-semibold mr-8">
                {label}
              </span>
            </React.Fragment>
          ))}
        </div>

        {/* Step Content */}
        <div
          ref={scrollRef}
          id="ad-creative-modal-scroll"
          className="flex-1 flex flex-col px-8 pt-2 pb-32 min-h-0 overflow-y-auto"
        >
          {/* Step 1 */}
          {step === 1 && (
            <>
              <p className="mb-4 text-gray-300 flex items-center gap-4">
                Select a product image to include in your creative.
                <span className="text-gray-400 text-sm flex items-center gap-3">
                  Selected {isSelected ? 1 : 0} / 1
                  <span className="flex items-center gap-1">
                    <span
                      className={`w-2.5 h-2.5 rounded-full ${
                        isSelected ? "bg-pink-500" : "bg-[#2B2B4A]"
                      }`}
                    />
                  </span>
                </span>
              </p>

              <input
                ref={step1FileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleStep1Upload}
              />

              {!filteredProjectAssets || filteredProjectAssets.length === 0 ? (
                <div className="text-white/70 text-center py-10 select-none">
                  No product images found. Analyze your product to extract images
                  first.
                </div>
              ) : (
                <div className="rounded-lg border border-[#23233a] bg-[#191930] px-4 py-3">
                  <div className="grid grid-cols-5 gap-4">
                    {/* Upload tile */}
                    <div
                      className={`relative rounded-lg overflow-hidden border-2 group transition
                        ${
                          step1UploadedAsset
                            ? selectedUrl === step1UploadedAsset.url
                              ? "border-pink-500 ring-2 ring-pink-600 cursor-pointer"
                              : "border-transparent hover:border-pink-400 cursor-pointer"
                            : "border-[#3A3A4A] border-dashed hover:border-pink-400 cursor-pointer"
                        }`}
                      onClick={() => {
                        if (step1UploadLoading) return;

                        if (step1UploadedAsset) {
                          setSelectedAsset(step1UploadedAsset);
                          return;
                        }

                        step1FileInputRef.current?.click();
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (step1UploadLoading) return;

                          if (step1UploadedAsset) {
                            setSelectedAsset(step1UploadedAsset);
                            return;
                          }

                          step1FileInputRef.current?.click();
                        }
                      }}
                      aria-label={
                        step1UploadedAsset ? "Uploaded asset" : "Upload image"
                      }
                    >
                      <div className="aspect-[4/5] bg-black relative">
                        {step1UploadedAsset ? (
                          <img
                            className="object-cover w-full h-full"
                            src={step1UploadedAsset.url}
                            alt="Uploaded asset"
                            loading="lazy"
                          />
                        ) : (
                          <div className="w-full h-full flex flex-col items-center justify-center bg-[#151529]">
                            <div className="w-12 h-12 rounded-full bg-[#23233a] border border-[#2B2B4A] flex items-center justify-center mb-3">
                              <Plus size={22} className="text-white" />
                            </div>
                            <div className="text-white font-semibold text-sm">
                              Upload image
                            </div>
                            <div className="text-gray-400 text-[11px] mt-1 px-3 text-center">
                              JPG, PNG, WEBP
                            </div>
                          </div>
                        )}

                        {step1UploadLoading && (
                          <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
                            <Loader2 className="animate-spin text-white" />
                          </div>
                        )}

                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </div>

                      {step1UploadedAsset && (
                        <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition">
                          <button
                            type="button"
                            className="px-2.5 py-1.5 rounded-md bg-black/70 hover:bg-black/85 text-white text-xs font-semibold border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (step1UploadLoading) return;
                              step1FileInputRef.current?.click();
                            }}
                            aria-label="Replace uploaded image"
                            disabled={step1UploadLoading}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="p-2 rounded-md bg-black/70 hover:bg-black/85 text-white border border-white/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              const uploadedUrl = step1UploadedAsset.url;
                              setStep1UploadedAsset(null);
                              if (selectedUrl === uploadedUrl) setSelectedAsset(null);
                            }}
                            aria-label="Remove uploaded image"
                            disabled={step1UploadLoading}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      )}
                    </div>

                    {filteredProjectAssets.map((asset, idx) => {
                      const url = getAssetUrl(asset);
                      const tileSelected = Boolean(
                        selectedUrl && url && selectedUrl === url
                      );

                      return (
                        <div
                          key={asset?.id || url || idx}
                          className={`relative rounded-lg overflow-hidden border-2 cursor-pointer group transition
                            ${
                              tileSelected
                                ? "border-pink-500 ring-2 ring-pink-600"
                                : "border-transparent hover:border-pink-400"
                            }`}
                          onClick={() => setSelectedAsset(asset)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSelectedAsset(asset);
                            }
                          }}
                          aria-label="Select asset"
                        >
                          <div className="aspect-[4/5] bg-black relative">
                            <img
                              src={url}
                              alt="Asset"
                              className="object-cover w-full h-full"
                              loading="lazy"
                              draggable={false}
                            />
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <div className="mt-2">
              <h2 className="text-xl font-bold text-white mb-2">Ad Texts</h2>
              <p className="text-gray-400 mb-4">
                Generated automatically from your product details — feel free to edit.
                Use the ↻ icons to regenerate a single field.
              </p>

              {error && <div className="mb-3 text-red-400 font-semibold">{error}</div>}

              <Field
                id="headline"
                label="Headline"
                value={headline}
                onChange={handleTextChange(setHeadline, LIMITS.headline)}
                loadingFlag={textsLoading || regenHeadlineLoading}
                limit={LIMITS.headline}
                placeholder="e.g., Fresh style, everyday comfort"
                onRegenerate={() => regenerateField("headline")}
                regenDisabled={regenHeadlineLoading}
              />

              <Field
                id="punchline"
                label="Punchline"
                value={punchline}
                onChange={handleTextChange(setPunchline, LIMITS.punchline)}
                loadingFlag={textsLoading || regenPunchlineLoading}
                limit={LIMITS.punchline}
                placeholder="e.g., Soft-knit & breathable all day"
                onRegenerate={() => regenerateField("punchline")}
                regenDisabled={regenPunchlineLoading}
              />

              <Field
                id="cta"
                label="Call to Action"
                value={cta}
                onChange={handleTextChange(setCta, LIMITS.cta)}
                loadingFlag={textsLoading || regenCtaLoading}
                limit={LIMITS.cta}
                placeholder="e.g., Shop Now"
                onRegenerate={() => regenerateField("cta")}
                regenDisabled={regenCtaLoading}
              />
            </div>
          )}

          {/* Step 3 (Template) */}
          {step === 3 && (
            <>
              <h2 className="text-xl font-bold text-white mb-4">Choose Template</h2>

              {loading && (
                <div className="text-gray-300 flex items-center gap-2">
                  <Loader2 className="animate-spin" size={18} />
                  <span>Loading templates...</span>
                </div>
              )}

              {error && <p className="text-red-400">{error}</p>}

              {!loading && !error && filteredTemplates.length === 0 && (
                <p className="text-gray-400">No templates match your filters.</p>
              )}

              <p className="text-xs text-white/50 mb-4 flex items-center gap-1">
                <Info size={14} /> Templates are loaded as you scroll.
              </p>

              {/* Filters row */}
              <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                <div className="flex flex-wrap items-start gap-4">
                  <SingleSelectDropdown
                    options={sizeOptions}
                    value={sizeFilter}
                    onChange={(val) => setSizeFilter(val || "all")}
                    label="Size"
                    placeholder="All sizes"
                    className="w-72"
                  />

                  {hasCollections && (
                    <SingleSelectDropdown
                      options={collectionOptions}
                      value={collectionFilter}
                      onChange={(val) => {
                        const next = val || "all";
                        setCollectionFilter(next);
                      }}
                      label="Collection"
                      placeholder="All collections"
                      className="w-72"
                    />
                  )}
                </div>

                <div className="flex flex-col items-end">
                  <span className="block text-xs mb-1 ml-1 opacity-0">Clear</span>
                  <button
                    onClick={() => {
                      setCollectionFilter("all");
                      setSizeFilter("all");
                    }}
                    className="px-3 py-2 bg-pink-600 rounded-md text-white hover:bg-pink-700 text-sm"
                    aria-label="Clear template filters"
                    type="button"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Grid */}
              <div className="grid grid-cols-5 gap-4 pb-4">
                {filteredTemplates.map((tpl) => {
                  const active = selectedTemplate?.id === tpl.id;

                  return (
                    <div
                      key={tpl.id}
                      className={`relative rounded-lg overflow-hidden border-2 group transition cursor-pointer
                        ${
                          active
                            ? "border-pink-500 ring-2 ring-pink-600"
                            : "border-transparent hover:border-pink-400"
                        }`}
                      onClick={() => setSelectedTemplate(tpl)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedTemplate(tpl);
                        }
                      }}
                      aria-label={`Select template ${tpl.name}`}
                      title={tpl.name}
                    >
                      <div className="aspect-[4/5] bg-black relative">
                        <img
                          src={tpl.preview_image}
                          alt={tpl.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          draggable={false}
                        />

                        <div className="absolute inset-0 bg-black/10 group-hover:bg-pink-600/10 transition" />
                      </div>

                      <div className="absolute top-2 left-2 right-2">
                        <div className="px-2 py-1 rounded-md bg-black/60 border border-white/10 text-white text-xs truncate">
                          {tpl.name}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Infinite loader trigger + spinner */}
              <div ref={loadMoreRef} className="h-10 w-full" />
              {(loadingMore || (hasMore && !loading && !error)) && (
                <div className="flex items-center justify-center py-4 text-white/80">
                  {loadingMore ? (
                    <div className="flex items-center gap-2">
                      <Loader2 size={18} className="animate-spin" />
                      <span className="text-sm">Loading more templates…</span>
                    </div>
                  ) : hasMore ? (
                    <div className="text-sm text-white/50">Scroll to load more…</div>
                  ) : null}
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <StepFooter
          left={
            <button
              onClick={() => setStep((s) => Math.max(1, s - 1))}
              className="rounded-lg font-semibold px-6 py-2 bg-[#2B2B4A] hover:bg-[#3D3D6E] text-white disabled:opacity-60 disabled:cursor-not-allowed"
              disabled={step === 1}
              type="button"
            >
              Previous
            </button>
          }
          right={
            step < 3 ? (
              <button
                onClick={() => setStep((s) => s + 1)}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                disabled={step === 1 && !selectedAsset}
                type="button"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleGenerate}
                className="rounded-lg bg-pink-600 font-semibold px-6 py-2 hover:bg-pink-700 text-white disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
                disabled={!selectedTemplate}
                type="button"
              >
                Generate Creative
              </button>
            )
          }
        />

        {error && (
          <div className="absolute left-0 right-0 bottom-[72px] text-red-400 text-center py-2 text-sm bg-black/20 border-t border-white/5">
            {error}
          </div>
        )}
      </div>
    </div>
  );
}

function SingleSelectDropdown({
  options,
  value = "",
  onChange,
  label,
  placeholder = "Select...",
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handle = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [open]);

  const getLabel = (val) => {
    if (!val) return null;
    const opt = options.find((o) => String(o.value) === String(val));
    return opt ? opt.label : val;
  };

  return (
    <div className={`relative ${className}`} ref={ref}>
      <label className="block text-xs text-gray-300 mb-1 ml-1">{label}</label>
      <button
        type="button"
        className={`
          flex w-full min-h-[38px] items-center px-3 py-1.5 rounded-lg bg-[#23233a] text-white border
          transition-all outline-none focus:ring-2 focus:ring-pink-600
          ${open ? "border-pink-500 ring-2 ring-pink-600" : "border-[#353550]"}
        `}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <div className="flex flex-1 items-center text-left">
          {value && value !== "all" ? (
            <span className="truncate">{getLabel(value)}</span>
          ) : (
            <span className={value === "all" ? "truncate" : "text-gray-400"}>
              {value === "all" ? getLabel("all") || placeholder : placeholder}
            </span>
          )}
        </div>
        <svg
          className={`ml-2 transition-transform ${open ? "rotate-180" : ""}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          width="18"
          height="18"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {open && (
        <ul
          className="absolute z-40 mt-1 w-full rounded-xl shadow-xl bg-[#212134] border border-[#353550] max-h-56 overflow-auto animate-fadeIn"
          role="listbox"
          tabIndex={-1}
        >
          {options.length === 0 && <li className="p-3 text-gray-400">No options</li>}
          {options.map((opt) => {
            const selected = String(value) === String(opt.value);
            return (
              <li
                key={opt.value}
                className={`cursor-pointer px-3 py-2 text-sm select-none ${
                  selected ? "bg-pink-600/10 text-pink-300" : "hover:bg-[#2e2e45]"
                }`}
                onClick={() => {
                  onChange(String(opt.value));
                  setOpen(false);
                }}
                role="option"
                aria-selected={selected}
              >
                {opt.label}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
